import React, { useEffect, useMemo, useState } from 'react';
import {
  Customer,
  Appointment,
  SystemConfig,
  CommunicationLog,
  QuestionnaireSubmission,
  MedicalCertificate,
  QuestionnaireTemplate,
  Service
} from '@/types';
import {
  Search,
  Mail,
  Phone,
  Calendar,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Smartphone,
  CheckCircle2,
  Clock,
  AlertCircle,
  Edit2,
  X,
  Save,
  Loader2,
  Key,
  MailCheck,
  Printer,
  Filter,
  ClipboardCheck
} from 'lucide-react';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { formatDobFromIso, formatDobInput, normalizeDobToIso } from '@/lib/formatters';
import { isValidUkPhone, normalizeUkPhone } from '@/lib/phone';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { Pagination } from '@shared/components/common/Pagination';
import { api } from '@/services/api';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  openCertificatePrintWindow,
  resolveCertificateClientName
} from '@/lib/certificateRenderer';
import { formatUkDate, formatUkTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { buildMapById } from '@/lib/collections';

interface CustomersTabProps {
  customers: Customer[];
  services?: Service[];
  questionnaires?: QuestionnaireTemplate[];
  onUpdateCustomer?: (customer: Customer) => void;
  onResetPassword?: (id: string) => void;
  onSendReminder?: (
    appointmentId: string,
    type: 'email' | 'sms',
    templateType?: string
  ) => Promise<void> | void;
  canEdit: boolean;
  targetCustomerId: string | null;
  onClearTargetCustomer: () => void;
  systemConfig?: SystemConfig;
}

type HistoryStatusFilter = 'all' | 'booked' | 'completed' | 'noshow' | 'cancelled';
type HistoryOutcomeFilter = 'all' | 'fit' | 'fit_restrictions' | 'referred' | 'unfit' | 'pending';

export const CustomersTab: React.FC<CustomersTabProps> = ({
  customers: initialCustomers,
  services = [],
  questionnaires = [],
  onUpdateCustomer,
  onResetPassword,
  onSendReminder,
  canEdit,
  targetCustomerId,
  onClearTargetCustomer,
  systemConfig
}) => {
  const formatDobForDisplay = (value?: string) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    return formatDobFromIso(datePart).replace(/\//g, '-');
  };
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const formatDobInputHyphen = (value: string) => formatDobInput(value).replace(/\//g, '-');
  const normalizeDobForSave = (value?: string) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    return normalizeDobToIso(datePart.replace(/-/g, '/'));
  };

  const [customers, setCustomers] = useState<Customer[]>(initialCustomers || []);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  const [customerSort, setCustomerSort] = useState<{
    sortBy: 'registeredAt' | 'name';
    sortDir: 'asc' | 'desc';
  }>({ sortBy: 'registeredAt', sortDir: 'desc' });
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const [historyItems, setHistoryItems] = useState<Appointment[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<HistoryStatusFilter>('all');
  const [historyOutcomeFilter, setHistoryOutcomeFilter] = useState<HistoryOutcomeFilter>('all');
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [reviewCertificate, setReviewCertificate] = useState<MedicalCertificate | null>(null);
  const [reviewSubmission, setReviewSubmission] = useState<QuestionnaireSubmission | null>(null);
  const [isLoadingReviewData, setIsLoadingReviewData] = useState(false);
  const [reviewDataError, setReviewDataError] = useState('');

  const [logItems, setLogItems] = useState<CommunicationLog[]>([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(25);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  type ReviewAnswerEntry = {
    key: string;
    label: string;
    value: unknown;
  };
  const QUESTION_DETAIL_SUFFIXES = new Set([
    'details',
    'detail',
    'comment',
    'comments',
    'commenttext',
    'detailstext',
    'note',
    'notes',
    'additionalinfo',
    'additionaldetails'
  ]);
  const QUESTION_DETAIL_SUFFIX_ORDER = [
    'details',
    'detail',
    'comment',
    'comments',
    'commenttext',
    'detailstext',
    'note',
    'notes',
    'additionalinfo',
    'additionaldetails'
  ];

  useEffect(() => {
    setPage(1);
  }, [search, customerSort.sortBy, customerSort.sortDir]);

  useEffect(() => {
    setPhoneTouched(false);
  }, [editingCustomer?.id]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingCustomers(true);
    setCustomersError('');
    api
      .listStaffCustomers({
        page,
        pageSize,
        q: search || undefined,
        sortBy: customerSort.sortBy,
        sortDir: customerSort.sortDir
      })
      .then((data) => {
        if (!isActive) return;
        const items = (data.items || []).map((item) => ({
          ...item,
          logs: item.logs || [],
          changeLogs: item.changeLogs || []
        }));
        setCustomers(items);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch((err: unknown) => {
        if (!isActive) return;
        setCustomers([]);
        setTotalCount(0);
        setCustomersError(normalizeErrorMessage(err, 'Failed to load customers.'));
      })
      .finally(() => {
        if (isActive) setIsLoadingCustomers(false);
      });

    return () => {
      isActive = false;
    };
  }, [page, pageSize, search, customerSort.sortBy, customerSort.sortDir]);

  const refreshCustomers = async () => {
    setIsLoadingCustomers(true);
    setCustomersError('');
    try {
      const data = await api.listStaffCustomers({
        page,
        pageSize,
        q: search || undefined,
        sortBy: customerSort.sortBy,
        sortDir: customerSort.sortDir
      });
      const items = (data.items || []).map((item) => ({
        ...item,
        logs: item.logs || [],
        changeLogs: item.changeLogs || []
      }));
      setCustomers(items);
      setTotalCount(Number(data.totalCount || 0));
    } catch (err: unknown) {
      setCustomers([]);
      setTotalCount(0);
      setCustomersError(normalizeErrorMessage(err, 'Failed to load customers.'));
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // If targetCustomerId is passed (e.g. from Bookings tab), select that customer
  useEffect(() => {
    if (targetCustomerId) {
      const target = customers.find((c) => c.id === targetCustomerId);
      if (target) setSelectedCustomer(target);
    }
  }, [targetCustomerId, customers]);

  useEffect(() => {
    setHistoryPage(1);
    setLogsPage(1);
    setHistorySearch('');
    setHistoryStatusFilter('all');
    setHistoryOutcomeFilter('all');
    setSelectedHistoryId(null);
    setReviewCertificate(null);
    setReviewSubmission(null);
    setReviewDataError('');
  }, [selectedCustomer?.id]);

  useEffect(() => {
    if (!selectedCustomer) {
      setHistoryItems([]);
      setHistoryTotalCount(0);
      return;
    }
    let isActive = true;
    setIsLoadingHistory(true);
    api
      .listStaffCustomerHistory(selectedCustomer.id, {
        page: historyPage,
        pageSize: historyPageSize
      })
      .then((data) => {
        if (!isActive) return;
        setHistoryItems(data.items || []);
        setHistoryTotalCount(Number(data.totalCount || 0));
      })
      .catch(() => {
        if (!isActive) return;
        setHistoryItems([]);
        setHistoryTotalCount(0);
      })
      .finally(() => {
        if (isActive) setIsLoadingHistory(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedCustomer?.id, historyPage, historyPageSize]);

  useEffect(() => {
    if (!selectedCustomer) {
      setLogItems([]);
      setLogsTotalCount(0);
      return;
    }
    let isActive = true;
    setIsLoadingLogs(true);
    api
      .listStaffCustomerLogs(selectedCustomer.id, {
        page: logsPage,
        pageSize: logsPageSize
      })
      .then((data) => {
        if (!isActive) return;
        setLogItems(data.items || []);
        setLogsTotalCount(Number(data.totalCount || 0));
      })
      .catch(() => {
        if (!isActive) return;
        setLogItems([]);
        setLogsTotalCount(0);
      })
      .finally(() => {
        if (isActive) setIsLoadingLogs(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedCustomer?.id, logsPage, logsPageSize]);

  const handleCloseDetail = () => {
    setSelectedCustomer(null);
    onClearTargetCustomer();
  };

  const toggleCustomerNameSort = () => {
    setCustomerSort((prev) => {
      if (prev.sortBy !== 'name') return { sortBy: 'name', sortDir: 'asc' };
      if (prev.sortDir === 'asc') return { sortBy: 'name', sortDir: 'desc' };
      return { sortBy: 'registeredAt', sortDir: 'desc' };
    });
  };

  const renderCustomerNameSortIcon = () => {
    if (customerSort.sortBy !== 'name') {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />;
    }
    return customerSort.sortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );
  };

  const editingPhoneValue = String(editingCustomer?.phone || '').trim();
  const showPhoneError = phoneTouched && editingPhoneValue && !isValidUkPhone(editingPhoneValue);

  const handleSaveProfile = async () => {
    if (!editingCustomer || !onUpdateCustomer) return;
    const phoneValue = String(editingCustomer.phone || '').trim();
    if (phoneValue && !isValidUkPhone(phoneValue)) {
      setPhoneTouched(true);
      showAlertModal('Please enter a valid UK phone number.');
      return;
    }
    setIsSaving(true);
    try {
      const normalizedCustomer = {
        ...editingCustomer,
        dob: normalizeDobForSave(editingCustomer.dob || ''),
        phone: phoneValue ? normalizeUkPhone(phoneValue) : ''
      };
      await onUpdateCustomer(normalizedCustomer);
      await refreshCustomers();
      setSelectedCustomer(normalizedCustomer);
      setEditingCustomer(null);
    } catch (err: unknown) {
      showAlertModal(`Error saving: ${normalizeErrorMessage(err, 'Unknown error')}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedCustomer || !onResetPassword) return;
    const confirmed = await showConfirmModal(
      `Send a password reset email to ${selectedCustomer.name}?`,
      {
        title: 'Send Password Reset',
        confirmText: 'Send',
        cancelText: 'Cancel',
        isDestructive: false
      }
    );
    if (!confirmed) return;
    await Promise.resolve(onResetPassword(selectedCustomer.id));
    showAlertModal('Reset email dispatched.');
  };

  const handleResendCertEmail = async (appId: string) => {
    if (!onSendReminder) return;
    const confirmed = await showConfirmModal(
      "Re-send the 'Certificate Issued' email to this patient?",
      {
        title: 'Resend Certificate Email',
        confirmText: 'Resend',
        cancelText: 'Cancel',
        isDestructive: false
      }
    );
    if (!confirmed) return;
    await Promise.resolve(onSendReminder(appId, 'email', 'certificate_issued'));
  };

  const handleViewCertificate = async (app: Appointment) => {
    if (!systemConfig) return;
    let cert = null;
    try {
      const certResult = await api.listStaffCertificates({
        appointmentId: app.id,
        page: 1,
        pageSize: 1
      });
      cert = certResult.items?.[0] || null;
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to load certificate.'));
      return;
    }

    if (!cert) {
      showAlertModal('Certificate not found for this appointment.');
      return;
    }

    const html = buildCertificateHtml({
      template: systemConfig.templates.certificateTemplate.body,
      systemConfig,
      appointment: app,
      certificate: cert,
      clientName: resolveCertificateClientName(app)
    });

    openCertificatePrintWindow(
      html,
      buildCertificateFileName({
        appointment: app,
        certificate: cert
      })
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle2 className="w-3 h-3 text-green-500" />;
      case 'queued':
        return <Clock className="w-3 h-3 text-amber-500" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return null;
    }
  };

  const toTitleCase = (value: string) =>
    value
      .replace(/_/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getOutcomeChipClass = (outcome?: string | null) => {
    switch (String(outcome || '').toLowerCase()) {
      case 'fit':
        return 'bg-green-50 text-green-700 border-green-100';
      case 'fit_restrictions':
        return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'referred':
        return 'bg-orange-50 text-orange-700 border-orange-100';
      case 'unfit':
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const formatFieldLabel = (rawKey: string) =>
    toTitleCase(rawKey.replace(/_details$/i, '').replace(/([a-z])([A-Z])/g, '$1 $2')) +
    (/_details$/i.test(rawKey) ? ' (Details)' : '');

  const formatFieldValue = (value: unknown) => {
    if (value == null) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    const text = String(value).trim();
    return text || '—';
  };
  const hasReadableValue = (value: unknown) => {
    if (value == null) return false;
    if (typeof value === 'string') return Boolean(value.trim());
    return true;
  };
  const getQuestionScopedSuffix = (key: string, questionId: string): string | null => {
    const normalizedKey = String(key || '')
      .trim()
      .toLowerCase();
    const normalizedQuestionId = String(questionId || '')
      .trim()
      .toLowerCase();
    if (
      !normalizedKey ||
      !normalizedQuestionId ||
      !normalizedKey.startsWith(normalizedQuestionId)
    ) {
      return null;
    }
    let suffix = normalizedKey.slice(normalizedQuestionId.length);
    if (!suffix) return '';
    if (suffix[0] === '_' || suffix[0] === '.' || suffix[0] === '-') {
      suffix = suffix.slice(1);
    }
    return suffix || null;
  };

  const normalizedHistorySearch = historySearch.trim().toLowerCase();
  const filteredHistoryItems = useMemo(() => {
    return historyItems.filter((app) => {
      if (historyStatusFilter !== 'all' && app.status !== historyStatusFilter) return false;
      const outcome = String(app.certificateSummary?.outcome || 'pending').toLowerCase();
      if (historyOutcomeFilter !== 'all' && outcome !== historyOutcomeFilter) return false;
      if (!normalizedHistorySearch) return true;
      const searchIndex = [
        app.id,
        app.customerName,
        app.companyName,
        app.serviceName,
        app.venueName,
        app.time,
        app.status,
        app.certificateSummary?.outcome
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchIndex.includes(normalizedHistorySearch);
    });
  }, [historyItems, historyOutcomeFilter, historyStatusFilter, normalizedHistorySearch]);

  const selectedHistoryItem = useMemo(
    () => filteredHistoryItems.find((item) => item.id === selectedHistoryId) || null,
    [filteredHistoryItems, selectedHistoryId]
  );

  const questionnaireById = useMemo(
    () => new Map((questionnaires || []).map((item) => [item.id, item])),
    [questionnaires]
  );
  const reviewTemplate = useMemo(() => {
    if (reviewSubmission?.templateId && questionnaireById.has(reviewSubmission.templateId)) {
      return questionnaireById.get(reviewSubmission.templateId) || null;
    }
    const fallbackTemplateId = servicesById.get(selectedHistoryItem?.serviceId || '')
      ?.questionnaireConfig?.templateId;
    if (fallbackTemplateId && questionnaireById.has(fallbackTemplateId)) {
      return questionnaireById.get(fallbackTemplateId) || null;
    }
    return null;
  }, [
    questionnaireById,
    reviewSubmission?.templateId,
    selectedHistoryItem?.serviceId,
    servicesById
  ]);
  const reviewAnswerEntries = useMemo<ReviewAnswerEntry[]>(() => {
    const answers = (reviewSubmission?.answers || {}) as Record<string, unknown>;
    const answerEntries = Object.entries(answers).filter(([, value]) => hasReadableValue(value));
    if (!answerEntries.length) return [];

    if (!reviewTemplate?.questions?.length) {
      return answerEntries.map(([key, value]) => ({
        key,
        label: formatFieldLabel(key),
        value
      }));
    }

    const consumedKeys = new Set<string>();
    const orderedEntries: ReviewAnswerEntry[] = [];

    reviewTemplate.questions.forEach((question) => {
      const questionEntry = answerEntries.find(
        ([key]) =>
          String(key || '')
            .trim()
            .toLowerCase() ===
          String(question.id || '')
            .trim()
            .toLowerCase()
      );
      if (questionEntry) {
        orderedEntries.push({
          key: questionEntry[0],
          label: question.label || formatFieldLabel(question.id),
          value: questionEntry[1]
        });
        consumedKeys.add(questionEntry[0]);
      }

      const scopedDetailEntries = answerEntries
        .map(([key, value], index) => ({
          key,
          value,
          index,
          suffix: getQuestionScopedSuffix(key, question.id)
        }))
        .filter(
          (entry) =>
            !consumedKeys.has(entry.key) &&
            Boolean(entry.suffix && QUESTION_DETAIL_SUFFIXES.has(entry.suffix))
        )
        .sort((left, right) => {
          const leftIndex = QUESTION_DETAIL_SUFFIX_ORDER.indexOf(String(left.suffix || ''));
          const rightIndex = QUESTION_DETAIL_SUFFIX_ORDER.indexOf(String(right.suffix || ''));
          if (leftIndex === rightIndex) return left.index - right.index;
          if (leftIndex === -1) return 1;
          if (rightIndex === -1) return -1;
          return leftIndex - rightIndex;
        });

      scopedDetailEntries.forEach((detailEntry) => {
        orderedEntries.push({
          key: detailEntry.key,
          label: `${question.detailsLabel || question.label || formatFieldLabel(question.id)} (Details)`,
          value: detailEntry.value
        });
        consumedKeys.add(detailEntry.key);
      });
    });

    const remainingEntries = answerEntries
      .filter(([key]) => !consumedKeys.has(key))
      .map(([key, value]) => ({
        key,
        label: formatFieldLabel(key),
        value
      }));

    return [...orderedEntries, ...remainingEntries];
  }, [reviewSubmission, reviewTemplate]);

  const historyCounts = useMemo(
    () => ({
      completed: historyItems.filter((item) => item.status === 'completed').length,
      withCertificate: historyItems.filter((item) => item.certificateSummary?.id).length,
      pendingReview: historyItems.filter(
        (item) => item.status === 'completed' && !item.certificateSummary?.id
      ).length
    }),
    [historyItems]
  );

  useEffect(() => {
    if (!filteredHistoryItems.length) {
      setSelectedHistoryId(null);
      setReviewCertificate(null);
      setReviewSubmission(null);
      return;
    }
    if (!selectedHistoryId || !filteredHistoryItems.some((item) => item.id === selectedHistoryId)) {
      setSelectedHistoryId(filteredHistoryItems[0].id);
    }
  }, [filteredHistoryItems, selectedHistoryId]);

  useEffect(() => {
    if (!selectedHistoryItem) {
      setReviewCertificate(null);
      setReviewSubmission(null);
      setReviewDataError('');
      return;
    }

    let isActive = true;
    setIsLoadingReviewData(true);
    setReviewDataError('');

    Promise.allSettled([
      api.listStaffCertificates({
        appointmentId: selectedHistoryItem.id,
        page: 1,
        pageSize: 1
      }),
      api.listStaffSubmissions({
        appointmentIds: selectedHistoryItem.id,
        page: 1,
        pageSize: 1
      })
    ])
      .then(([certResult, submissionResult]) => {
        if (!isActive) return;

        const errors: string[] = [];

        if (certResult.status === 'fulfilled') {
          setReviewCertificate(certResult.value.items?.[0] || null);
        } else {
          setReviewCertificate(null);
          if (selectedHistoryItem.certificateSummary?.id) {
            errors.push('Certificate details are currently unavailable.');
          }
        }

        if (submissionResult.status === 'fulfilled') {
          setReviewSubmission(submissionResult.value.items?.[0] || null);
        } else {
          setReviewSubmission(null);
          if (selectedHistoryItem.questionnaireStatus === 'submitted') {
            errors.push('Questionnaire answers could not be loaded.');
          }
        }

        setReviewDataError(errors.join(' '));
      })
      .finally(() => {
        if (isActive) setIsLoadingReviewData(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    selectedHistoryItem?.id,
    selectedHistoryItem?.certificateSummary?.id,
    selectedHistoryItem?.questionnaireStatus
  ]);

  type ResolvedCertificate = Omit<Partial<MedicalCertificate>, 'outcome'> & {
    id: string;
    outcome?: string | null;
  };
  const resolvedCertificate: ResolvedCertificate | null =
    reviewCertificate ||
    (selectedHistoryItem?.certificateSummary
      ? {
          id: selectedHistoryItem.certificateSummary.id,
          appointmentId: selectedHistoryItem.id,
          customerEmail: selectedHistoryItem.customerEmail,
          issuedAt: selectedHistoryItem.certificateSummary.issuedAt || '',
          validUntil: selectedHistoryItem.certificateSummary.validUntil || '',
          technicianName: '',
          outcome: selectedHistoryItem.certificateSummary.outcome || null
        }
      : null);

  return (
    <div className="animate-fade-in h-[calc(100vh-200px)] flex flex-col">
      {/* Edit Customer Modal */}
      {editingCustomer && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up border border-slate-200">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800">Edit Patient Profile</h3>
              <button
                onClick={() => setEditingCustomer(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  Full Name
                </label>
                <input
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  value={editingCustomer.name}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="DD-MM-YYYY"
                    maxLength={10}
                    className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                    value={editingCustomer.dob || ''}
                    onChange={(e) =>
                      setEditingCustomer({
                        ...editingCustomer,
                        dob: formatDobInputHyphen(e.target.value)
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                    Job Title
                  </label>
                  <input
                    className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                    value={editingCustomer.jobTitle || ''}
                    onChange={(e) =>
                      setEditingCustomer({ ...editingCustomer, jobTitle: e.target.value })
                    }
                    placeholder="e.g. Crane Op"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  value={editingCustomer.email}
                  onChange={(e) =>
                    setEditingCustomer({ ...editingCustomer, email: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  value={editingCustomer.phone || ''}
                  onChange={(e) =>
                    setEditingCustomer({ ...editingCustomer, phone: e.target.value })
                  }
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setPhoneTouched(true);
                    if (value && isValidUkPhone(value)) {
                      setEditingCustomer({ ...editingCustomer, phone: normalizeUkPhone(value) });
                    }
                  }}
                />
                <p className="mt-1 text-[10px] text-slate-400">
                  UK numbers only, e.g. 07123 456789 or +44 7123 456789.
                </p>
                {showPhoneError && (
                  <p className="mt-1 text-xs font-semibold text-red-600">
                    Enter a valid UK phone number.
                  </p>
                )}
              </div>
            </div>
            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setEditingCustomer(null)}
                className="px-6 py-2 text-slate-500 font-bold hover:bg-white rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="bg-teal-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-teal-700 flex items-center gap-2 shadow-lg disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCustomer ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-full overflow-hidden">
          <div className="p-6 border-b flex justify-between items-start bg-slate-50">
            <div>
              <button
                onClick={handleCloseDetail}
                className="text-sm text-slate-500 hover:text-slate-800 mb-2 flex items-center gap-1"
              >
                ← Back to List
              </button>
              <div className="flex items-center gap-3">
                <h3 className="text-2xl font-bold text-slate-900">{selectedCustomer.name}</h3>
                <div className="flex gap-1">
                  {canEdit && (
                    <button
                      onClick={() =>
                        setEditingCustomer({
                          ...selectedCustomer,
                          dob: formatDobForDisplay(selectedCustomer.dob || ''),
                          phone: selectedCustomer.phone || ''
                        })
                      }
                      className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors border border-transparent hover:border-teal-100"
                      title="Edit Record"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={handleResetPassword}
                    className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-transparent hover:border-orange-100"
                    title="Send Password Reset"
                  >
                    <Key className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600 mt-1">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4 text-teal-600" /> {selectedCustomer.email}
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4 text-teal-600" /> {selectedCustomer.phone}
                </span>
                {selectedCustomer.dob && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-teal-600" /> DOB:{' '}
                    {formatDobForDisplay(selectedCustomer.dob || '')}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2 uppercase font-black tracking-widest">
                System ID: {selectedCustomer.id}
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border text-center min-w-[100px] shadow-sm">
              <div className="text-2xl font-bold text-teal-600">{historyTotalCount}</div>
              <div className="text-xs text-slate-500 uppercase font-bold">Bookings</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              <div className="xl:col-span-1">
                <h4 className="font-black text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Clinical History & Outcomes
                </h4>
                <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5" /> Review Filters
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    <input
                      type="text"
                      placeholder="Search history..."
                      className="w-full border rounded-lg px-3 py-2 text-xs bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                    />
                    <select
                      className="w-full border rounded-lg px-2 py-2 text-xs bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                      value={historyStatusFilter}
                      onChange={(e) =>
                        setHistoryStatusFilter(e.target.value as HistoryStatusFilter)
                      }
                    >
                      <option value="all">All statuses</option>
                      <option value="booked">Booked</option>
                      <option value="completed">Completed</option>
                      <option value="noshow">No show</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <select
                      className="w-full border rounded-lg px-2 py-2 text-xs bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                      value={historyOutcomeFilter}
                      onChange={(e) =>
                        setHistoryOutcomeFilter(e.target.value as HistoryOutcomeFilter)
                      }
                    >
                      <option value="all">All outcomes</option>
                      <option value="fit">Fit</option>
                      <option value="fit_restrictions">Fit with restrictions</option>
                      <option value="referred">Referred</option>
                      <option value="unfit">Unfit</option>
                      <option value="pending">Pending certificate</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-slate-100 text-slate-600 border border-slate-200">
                      Completed: {historyCounts.completed}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-teal-50 text-teal-700 border border-teal-100">
                      Certified: {historyCounts.withCertificate}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-100">
                      Awaiting review: {historyCounts.pendingReview}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {isLoadingHistory ? (
                    <p className="text-slate-500 italic text-sm">Loading history...</p>
                  ) : historyItems.length === 0 ? (
                    <p className="text-slate-500 italic text-sm">No booking history found.</p>
                  ) : filteredHistoryItems.length === 0 ? (
                    <p className="text-slate-500 italic text-sm">
                      No records match the review filters.
                    </p>
                  ) : (
                    filteredHistoryItems.map((app) => {
                      const cert = app.certificateSummary;
                      const isSelected = selectedHistoryId === app.id;
                      return (
                        <div
                          key={app.id}
                          className={`bg-white border rounded-xl p-4 shadow-sm group transition-all ${
                            isSelected
                              ? 'border-teal-400 ring-2 ring-teal-100'
                              : 'hover:border-teal-300'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2 gap-3">
                            <div>
                              <div className="font-bold text-slate-900 flex items-center gap-2">
                                {formatUkDate(app.date, '—')}
                                <span className="text-slate-200">|</span>
                                <span className="text-sm font-medium">{app.time}</span>
                              </div>
                              {app.companyName && (
                                <div className="text-[10px] text-slate-500 mt-1">
                                  Company: {app.companyName}
                                </div>
                              )}
                              {app.serviceName && (
                                <div className="text-[10px] text-slate-500">
                                  Service: {app.serviceName}
                                </div>
                              )}
                              <div className="text-xs font-bold text-teal-700 mt-1 uppercase tracking-tighter">
                                ID: {app.id}
                              </div>
                            </div>
                            <StatusBadge
                              status={app.status}
                              className="text-[9px] tracking-widest px-2 py-0.5"
                            />
                          </div>

                          <div className="mt-4 pt-4 border-t border-slate-50 flex items-center justify-between gap-2">
                            <span
                              className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg border ${getOutcomeChipClass(cert?.outcome || 'pending')}`}
                            >
                              {toTitleCase(String(cert?.outcome || 'pending'))}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setSelectedHistoryId(app.id)}
                                className={`px-2.5 py-1 text-[10px] rounded-lg border font-black uppercase tracking-wider transition-all ${
                                  isSelected
                                    ? 'bg-teal-600 text-white border-teal-600'
                                    : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50'
                                }`}
                                title="Open Medical Data Review"
                              >
                                Review
                              </button>
                              {cert && (
                                <>
                                  <button
                                    onClick={() => handleResendCertEmail(app.id)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                    title="Resend Certificate Email"
                                  >
                                    <MailCheck className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleViewCertificate(app)}
                                    className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                                    title="Print/Download Certificate"
                                  >
                                    <Printer className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <Pagination
                  page={historyPage}
                  pageSize={historyPageSize}
                  totalCount={historyTotalCount}
                  onPageChange={setHistoryPage}
                  onPageSizeChange={(size) => {
                    setHistoryPageSize(size);
                    setHistoryPage(1);
                  }}
                  isLoading={isLoadingHistory}
                />
              </div>

              <div className="xl:col-span-1">
                <h4 className="font-black text-xs text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" /> Medical Data Review
                </h4>
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm min-h-[460px]">
                  {!selectedHistoryItem ? (
                    <p className="text-sm text-slate-500 italic">
                      Select a booking from clinical history to review its medical data.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-bold text-slate-900 text-sm">
                            {formatUkDate(selectedHistoryItem.date, '—')} at{' '}
                            {selectedHistoryItem.time}
                          </p>
                          <p className="text-xs text-slate-500">
                            Ref: {selectedHistoryItem.id}
                            {selectedHistoryItem.serviceName
                              ? ` | ${selectedHistoryItem.serviceName}`
                              : ''}
                          </p>
                        </div>
                        <StatusBadge
                          status={selectedHistoryItem.status}
                          className="text-[9px] tracking-widest px-2 py-0.5"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                          Certificate Outcome
                        </span>
                        <span
                          className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-lg border ${getOutcomeChipClass(resolvedCertificate?.outcome || 'pending')}`}
                        >
                          {toTitleCase(String(resolvedCertificate?.outcome || 'pending'))}
                        </span>
                      </div>

                      {isLoadingReviewData && (
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Loading linked medical data...
                        </div>
                      )}
                      {reviewDataError && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                          {reviewDataError}
                        </div>
                      )}

                      <div className="rounded-lg border border-slate-200 p-3">
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                          Appointment Snapshot
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                          <div>
                            <span className="text-slate-400 block">Location</span>
                            {selectedHistoryItem.venueName || '-'}
                          </div>
                          <div>
                            <span className="text-slate-400 block">Company</span>
                            {selectedHistoryItem.companyName || 'Individual'}
                          </div>
                          <div>
                            <span className="text-slate-400 block">Questionnaire</span>
                            {toTitleCase(selectedHistoryItem.questionnaireStatus || 'not_required')}
                          </div>
                          <div>
                            <span className="text-slate-400 block">Booking Status</span>
                            {toTitleCase(selectedHistoryItem.status)}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3">
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                          Clinical Notes
                        </p>
                        <div className="space-y-2">
                          <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5">
                            <p className="text-[10px] font-black uppercase text-slate-400">
                              Technician Notes
                            </p>
                            <p className="text-xs text-slate-700 whitespace-pre-wrap">
                              {selectedHistoryItem.technicianNotes?.trim() ||
                                'No technician notes recorded.'}
                            </p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5">
                            <p className="text-[10px] font-black uppercase text-slate-400">
                              Staff Private Note
                            </p>
                            <p className="text-xs text-slate-700 whitespace-pre-wrap">
                              {selectedHistoryItem.staffPrivateNote?.trim() || 'No private note.'}
                            </p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5">
                            <p className="text-[10px] font-black uppercase text-slate-400">
                              Technician Private Reply
                            </p>
                            <p className="text-xs text-slate-700 whitespace-pre-wrap">
                              {selectedHistoryItem.technicianPrivateReply?.trim() ||
                                'No private reply.'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3">
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                          Certificate Details
                        </p>
                        {resolvedCertificate ? (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700">
                              <div>
                                <span className="text-slate-400 block">Issued</span>
                                {resolvedCertificate.issuedAt
                                  ? formatUkDate(resolvedCertificate.issuedAt, '-')
                                  : '-'}
                              </div>
                              <div>
                                <span className="text-slate-400 block">Valid Until</span>
                                {resolvedCertificate.validUntil
                                  ? formatUkDate(resolvedCertificate.validUntil, '-')
                                  : '-'}
                              </div>
                              <div>
                                <span className="text-slate-400 block">Drug Test</span>
                                {resolvedCertificate.drugTestOutcome
                                  ? toTitleCase(resolvedCertificate.drugTestOutcome)
                                  : 'Not recorded'}
                              </div>
                              <div>
                                <span className="text-slate-400 block">Certificate ID</span>
                                {resolvedCertificate.id}
                              </div>
                            </div>
                            {resolvedCertificate.restrictions && (
                              <p className="text-xs text-slate-700 mt-2">
                                <span className="text-slate-400">Restrictions: </span>
                                {resolvedCertificate.restrictions}
                              </p>
                            )}
                            {resolvedCertificate.advice && (
                              <p className="text-xs text-slate-700 mt-1">
                                <span className="text-slate-400">Advice: </span>
                                {resolvedCertificate.advice}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-3">
                              <button
                                onClick={() => handleViewCertificate(selectedHistoryItem)}
                                disabled={!systemConfig}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-teal-700 border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Printer className="w-3.5 h-3.5" /> View Certificate
                              </button>
                              <button
                                onClick={() => handleResendCertEmail(selectedHistoryItem.id)}
                                disabled={!onSendReminder}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <MailCheck className="w-3.5 h-3.5" /> Resend Certificate Email
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-slate-500 italic">
                            No certificate linked to this appointment yet.
                          </p>
                        )}
                      </div>

                      <div className="rounded-lg border border-slate-200 p-3">
                        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">
                          Questionnaire Answers
                        </p>
                        {reviewAnswerEntries.length > 0 ? (
                          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                            {reviewAnswerEntries.map((entry) => (
                              <div
                                key={entry.key}
                                className="bg-slate-50 border border-slate-100 rounded-lg p-2.5"
                              >
                                <p className="text-[10px] font-black uppercase text-slate-400">
                                  {entry.label}
                                </p>
                                <p className="text-xs text-slate-700 whitespace-pre-wrap break-words">
                                  {formatFieldValue(entry.value)}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : selectedHistoryItem.questionnaireStatus === 'pending' ? (
                          <p className="text-xs text-slate-500 italic">
                            Questionnaire is pending and has not been submitted.
                          </p>
                        ) : selectedHistoryItem.questionnaireStatus === 'not_required' ? (
                          <p className="text-xs text-slate-500 italic">
                            Questionnaire was not required for this appointment.
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500 italic">
                            No questionnaire answers available for review.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="xl:col-span-1">
                <h4 className="font-black text-xs text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" /> Dispatch Logs
                </h4>
                <div className="space-y-4">
                  {isLoadingLogs ? (
                    <p className="text-slate-500 italic text-sm">Loading logs...</p>
                  ) : logItems.length === 0 ? (
                    <p className="text-slate-500 italic text-sm">No communications recorded.</p>
                  ) : (
                    logItems.map((log) => {
                      const timestamp = log.sentAt || log.createdAt;
                      return (
                        <div
                          key={log.id}
                          className="bg-white border rounded-xl p-4 shadow-sm group hover:border-blue-200 transition-all"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <div
                                className={`p-1.5 rounded-lg ${log.type === 'email' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}
                              >
                                {log.type === 'email' ? (
                                  <Mail className="w-3.5 h-3.5" />
                                ) : (
                                  <Smartphone className="w-3.5 h-3.5" />
                                )}
                              </div>
                              <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                                  {log.type}
                                </p>
                                <p className="text-xs font-bold text-slate-700 mt-1">
                                  {log.subject ||
                                    (log.type === 'sms' ? 'SMS Message' : 'No Subject')}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="text-slate-400 text-[10px] font-bold block">
                                {timestamp ? formatUkTime(timestamp, '—') : '—'}
                              </span>
                              <span className="text-slate-400 text-[10px] block">
                                {formatUkDate(timestamp, '—', { preserveTimeZone: true })}
                              </span>
                            </div>
                          </div>
                          <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-xs text-slate-600 leading-relaxed italic line-clamp-2 group-hover:line-clamp-none transition-all">
                            "{log.content}"
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                              {getStatusIcon(log.status)}
                              <span className="text-[9px] font-black uppercase text-slate-500">
                                {log.status}
                              </span>
                            </div>
                            <span className="text-[9px] font-mono text-slate-300">
                              Log ID: {log.id}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <Pagination
                  page={logsPage}
                  pageSize={logsPageSize}
                  totalCount={logsTotalCount}
                  onPageChange={setLogsPage}
                  onPageSizeChange={(size) => {
                    setLogsPageSize(size);
                    setLogsPage(1);
                  }}
                  isLoading={isLoadingLogs}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-slate-800">Customer Database</h3>
              <p className="text-xs text-slate-500">
                Manage patient records and audit communication history.
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search customers..."
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-64 bg-white text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 overflow-hidden">
            <div className="overflow-y-auto h-full">
              <Table className="w-full text-left">
                <THead className="bg-slate-50 border-b sticky top-0 z-10">
                  <TR>
                    <TH className="font-bold text-[10px] uppercase tracking-widest text-slate-500">
                      <button
                        type="button"
                        onClick={toggleCustomerNameSort}
                        className="inline-flex items-center gap-1.5 hover:text-slate-800"
                        title="Toggle patient name sort"
                      >
                        Patient
                        {renderCustomerNameSortIcon()}
                      </button>
                    </TH>
                    <TH className="font-bold text-[10px] uppercase tracking-widest text-slate-500">
                      Contact Details
                    </TH>
                    <TH className="font-bold text-[10px] uppercase tracking-widest text-slate-500">
                      Registration
                    </TH>
                    <TH
                      align="center"
                      className="font-bold text-[10px] uppercase tracking-widest text-slate-500"
                    >
                      Visits
                    </TH>
                    <TH
                      align="right"
                      className="font-bold text-[10px] uppercase tracking-widest text-slate-500"
                    >
                      Action
                    </TH>
                  </TR>
                </THead>
                <TBody className="divide-y divide-slate-100">
                  {isLoadingCustomers ? (
                    <EmptyRow colSpan={5} className="p-8 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-10 h-10 text-slate-200 animate-spin" />
                        <p className="font-bold text-slate-800">Loading customers...</p>
                      </div>
                    </EmptyRow>
                  ) : customersError ? (
                    <EmptyRow colSpan={5} className="p-8 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <AlertCircle className="w-10 h-10 text-slate-200" />
                        <p className="font-bold text-slate-800">{customersError}</p>
                      </div>
                    </EmptyRow>
                  ) : customers.length === 0 ? (
                    <EmptyRow colSpan={5} className="p-8 text-center text-slate-500 italic">
                      No customers found matching search criteria.
                    </EmptyRow>
                  ) : (
                    customers.map((c) => (
                      <TR key={c.id} className="hover:bg-slate-50 transition-colors group">
                        <TD className="font-bold text-slate-900">{c.name}</TD>
                        <TD className="text-sm text-slate-600">
                          <div className="flex items-center gap-2 font-medium">{c.email}</div>
                          <div className="text-[10px] text-slate-400 font-bold">{c.phone}</div>
                        </TD>
                        <TD className="text-sm text-slate-500 font-medium">
                          {formatUkDate(c.registeredAt, '—', { preserveTimeZone: true })}
                        </TD>
                        <TD align="center">
                          <span className="bg-slate-100 px-2.5 py-1 rounded-full text-[10px] font-black text-slate-600 border border-slate-200">
                            {c.visitCount ?? 0}
                          </span>
                        </TD>
                        <TD align="right">
                          <button
                            onClick={() => {
                              setSelectedCustomer(c);
                              onClearTargetCustomer();
                            }}
                            className="bg-white border border-slate-200 text-slate-700 px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-all shadow-sm"
                          >
                            Manage Record
                          </button>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            isLoading={isLoadingCustomers}
          />
        </>
      )}
    </div>
  );
};
